/**
 * BetaOptInCard — a small toggle card on the member Dashboard that lets
 * users opt into the beta-tester program. When enabled, `GET /api/tools/available`
 * includes tools whose status is "beta" (in addition to the always-visible
 * "live" tools).
 *
 * State round-trips through:
 *   GET  /api/user/beta-opt-in  → { beta_tester: bool }
 *   POST /api/user/beta-opt-in  → { enabled: bool }
 */

import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Card, CardContent } from './ui/card';
import { Switch } from './ui/switch';
import { FlaskConical } from 'lucide-react';

export default function BetaOptInCard() {
  const { API, user } = useContext(AuthContext);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/user/beta-opt-in`);
        if (!cancelled) setEnabled(Boolean(data?.beta_tester));
      } catch {
        // Non-fatal — user simply sees the toggle in the off state.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API]);

  const toggle = async (next) => {
    setSaving(true);
    // Optimistic flip — revert on failure.
    setEnabled(next);
    try {
      const { data } = await axios.post(`${API}/user/beta-opt-in`, { enabled: next });
      setEnabled(Boolean(data?.beta_tester));
      toast.success(
        next ? 'Beta tools unlocked — refresh a tools page to see them.' : 'Beta tools hidden.',
      );
    } catch (e) {
      setEnabled(!next);
      toast.error(e?.response?.data?.detail || 'Could not update beta preference');
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <Card className="border-primary/30" data-testid="beta-opt-in-card">
      <CardContent className="p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <FlaskConical className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-bold text-primary">
            Beta program
          </div>
          <div className="font-bold text-base mt-0.5">Try beta tools</div>
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
            Get early access to tools still in testing. They may change or move without notice, and we love bug reports.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <Switch
            checked={enabled}
            onCheckedChange={toggle}
            disabled={loading || saving}
            data-testid="beta-opt-in-switch"
            aria-label="Toggle beta tools"
          />
          <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
            {loading ? '…' : enabled ? 'On' : 'Off'}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
